import { combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs';
import { first } from 'lodash-es';
import { BaseGraphElementsProvider } from './graph-elements/base-provider';
import { filterOnPropertyObservable } from '../utils/rxjs';
import { distanceConverter } from '../utils/formatting';
import { SensorDataSetGraph } from './graph';

export const ValueScaleEquation = Object.freeze({
    VIBRATION: 'vibration',
    ALARM: 'alarm',
    MAX: 'max',
});

function maxValuePerDataType$(graphs) {
    return combineLatest(
        // Collect active plugins from all graph inputs:
        graphs
            // `graph` can also be a table; filter non-graph instances.
            .filter((graph) => graph instanceof SensorDataSetGraph)
            .map((graph) => graph.activePlugins$)
    ).pipe(
        map((pluginsPerGraph) =>
            pluginsPerGraph
                // Concatenate all plugins from each graph into a single array.
                .flat()
                // Retain only plugins that provide graph elements.
                .filter((plugin) => plugin instanceof BaseGraphElementsProvider)
        ),
        // Filter out inactive plugins.
        switchMap((plugins) => filterOnPropertyObservable(plugins, 'isActive$')),
        // Keep unique `graphElements$` observables; some plugins share the same `graphElements$`.
        map((plugins) => Array.from(new Set(plugins.map((plugin) => plugin.graphElements$)))),
        // Listen to all `graphElements$` observables.
        switchMap((arrayOfGraphElements$) => combineLatest(arrayOfGraphElements$)),
        map((arrayOfElementArrays) =>
            arrayOfElementArrays.reduce((accumulator, elements) => {
                // Get the dataType from the first element in the array
                // (this dataType will be consistent across all entries).
                const firstElement = first(elements);
                const dataType = firstElement && firstElement.dataType;

                // Skip elements without a dataType, like events.
                if (dataType) {
                    // Determine the max value for each dataType and store it.
                    accumulator[dataType] = Math.max(
                        ...elements.map((element) => element.value),
                        accumulator[dataType] ?? 0
                    );
                }

                return accumulator;
            }, {})
        )
    );
}

function maxAlarm$(dataSet) {
    return combineLatest([dataSet.alarmLines$, dataSet.categoryLine$]).pipe(
        map(([alarmLines, categoryLine]) => {
            const allLines = [...alarmLines, categoryLine];
            const maxPerLine = allLines.map((line) =>
                Array.isArray(line)
                    ? Math.max(
                          ...line.map((objOrArray) =>
                              Array.isArray(objOrArray) ? objOrArray[0] : objOrArray.vkar
                          )
                      )
                    : 0
            );
            return Math.max(...maxPerLine);
        })
    );
}

const equations = {
    [ValueScaleEquation.VIBRATION](n) {
        return n.maxValuePerDataType$.pipe(
            map((maxValuePerDataType) => maxValuePerDataType[n.dataType] * 1.2)
        );
    },
    [ValueScaleEquation.ALARM](n) {
        return n.maxAlarm$.pipe(map((maxAlarm) => maxAlarm * 1.2));
    },
    [ValueScaleEquation.MAX](n) {
        return combineLatest([n.maxAlarm$, n.maxValuePerDataType$]).pipe(
            map(
                ([maxAlarm, maxValuePerDataType]) =>
                    Math.max(maxAlarm, maxValuePerDataType[n.dataType] ?? 0) * 1.2
            )
        );
    },
};

export function initAutoScaling(valueScale, dataSet, scales, graphs, valueScaleEquation, dataType) {
    // Perform auto-scaling only if no preferred valueScale is set.
    if (valueScale) {
        // Preferred valueScale is set, skip auto-scaling.
        return;
    }

    equations[valueScaleEquation]({
        dataType,
        maxValuePerDataType$: maxValuePerDataType$(graphs),
        maxAlarm$: maxAlarm$(dataSet),
    })
        .pipe(distinctUntilChanged())
        .subscribe((calculatedValueScale) => {
            if (!calculatedValueScale) {
                // Auto-scaling failed; no data available.
                return;
            }
            scales.set(dataType, distanceConverter(calculatedValueScale));
        });
}
